import { EmployeeSelect, DepartmentSelect } from '@/components/selects';
import { Select } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker';
import { cn } from '@/utils/cn';

export interface HrWorkforceFilters {
  employeeId: string;
  departmentId: string;
  status: string;
  leaveTypeId?: string;
  dateFrom: string;
  dateTo: string;
}

interface HrWorkforceFilterBarProps {
  filters: HrWorkforceFilters;
  onChange: (patch: Partial<HrWorkforceFilters>) => void;
  mode: 'attendance' | 'leave';
  statusOptions: { value: string; label: string }[];
  leaveTypeOptions?: { value: string; label: string }[];
  className?: string;
}

/** Equal-width columns in a single row; min-w-0 allows truncate inside dropdown triggers. */
const FILTER_CELL = 'min-w-0 flex-1';

export function HrWorkforceFilterBar({
  filters,
  onChange,
  mode,
  statusOptions,
  leaveTypeOptions,
  className,
}: HrWorkforceFilterBarProps) {
  return (
    <div
      className={cn(
        'sticky top-0 z-20 mb-4 overflow-visible rounded-lg border border-slate-200/80 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900',
        className,
      )}
    >
      <div className="flex flex-nowrap items-center gap-2 overflow-visible">
        <EmployeeSelect
          label=""
          className={FILTER_CELL}
          value={filters.employeeId}
          onChange={(v) => onChange({ employeeId: v })}
          placeholder="All employees"
          hrScoped
        />
        <DepartmentSelect
          label=""
          className={FILTER_CELL}
          value={filters.departmentId}
          onChange={(v) => onChange({ departmentId: v })}
          placeholder="All departments"
        />
        <Select
          className={FILTER_CELL}
          value={filters.status}
          onChange={(v) => onChange({ status: v })}
          placeholder="All statuses"
          options={[{ value: '', label: 'All statuses' }, ...statusOptions]}
        />
        {mode === 'leave' && leaveTypeOptions && (
          <Select
            className={FILTER_CELL}
            value={filters.leaveTypeId ?? ''}
            onChange={(v) => onChange({ leaveTypeId: v })}
            placeholder="All types"
            options={[{ value: '', label: 'All types' }, ...leaveTypeOptions]}
          />
        )}
        <DatePicker
          className={FILTER_CELL}
          value={filters.dateFrom}
          onChange={(v) => onChange({ dateFrom: v })}
          placeholder="From"
          max={filters.dateTo || undefined}
        />
        <span
          className="shrink-0 select-none px-0.5 text-sm text-slate-400 dark:text-slate-500"
          aria-hidden
        >
          –
        </span>
        <DatePicker
          className={FILTER_CELL}
          value={filters.dateTo}
          onChange={(v) => onChange({ dateTo: v })}
          placeholder="To"
          min={filters.dateFrom || undefined}
        />
      </div>
    </div>
  );
}
